import React, { useEffect, useRef, useState } from "react";
import {
  servingApi,
  type HitlItem,
} from "../../../api/serving";
import { chassis, LANG_LABELS } from "./shared/chassis";
import { useT, useUiLang } from "../../../stores/uiLang";
import { REVIEW_T } from "../../../i18n/teacherDashboard";

// Run E2 J6 (D10): one review queue for both doors.
//  - Teacher dashboard tab "Review answers" (embedded, any signed-in teacher).
//  - /lti/hitl-queue (Canvas launch, full page, passes the ltik for passback).
// The teacher listens to spoken answers, reads written answers, picks a score
// on the item's rubric (0 to the item max) and releases it. Labels: REVIEW_T.

export interface HitlQueueProps {
  /** Optional chapter filter from the launch context; omit to see all chapters. */
  chapterId?: string | null;
  /** Fresh ltik from the queue's own LTI launch — passed to releaseScore for AGS passback. */
  ltik?: string | null;
  /** Inside the teacher dashboard: no full-page background or min height. */
  embedded?: boolean;
}

type Draft = { score: number | null; feedback: string };

const isSpoken = (it: HitlItem) => it.response_mode === "audio";

// Server-authoritative per-item max (from the rubric scale on the backend).
const maxFor = (it: HitlItem) =>
  typeof it.max_score === "number" && it.max_score > 0 ? it.max_score : 1;

// Score levels, top score first. Uses the rubric's criteria when they match the
// item max; otherwise plain 0..max levels with no descriptor.
function levelsFor(it: HitlItem): { score: number; descriptor: string | null }[] {
  const max = maxFor(it);
  const crit = it.rubric?.criteria ?? [];
  const byScore = new Map(crit.map((c) => [Number(c.score), c.descriptor]));
  const out: { score: number; descriptor: string | null }[] = [];
  for (let s = Math.floor(max); s >= 0; s--) out.push({ score: s, descriptor: byScore.get(s) ?? null });
  return out;
}

// The AI proposal is on a 0-100 scale; map it onto the item's scale.
function aiSuggestion(it: HitlItem): number | null {
  if (it.ai_preliminary_score == null) return null;
  const max = maxFor(it);
  const v = Math.round((Number(it.ai_preliminary_score) / 100) * max);
  return Number.isFinite(v) ? Math.min(max, Math.max(0, v)) : null;
}

const HitlQueue: React.FC<HitlQueueProps> = ({ chapterId, ltik, embedded = false }) => {
  const t = useT(REVIEW_T);
  const lang = useUiLang();
  const [items, setItems] = useState<HitlItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [released, setReleased] = useState<Record<string, number>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // WS2 Rider: read-only per-teacher AI auto-release flag (plumbing only).
  const [aiAutoRelease, setAiAutoRelease] = useState<boolean | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    servingApi
      .getTeacherSettings()
      .then((s) => {
        if (!cancelled) setAiAutoRelease(s.ai_auto_release);
      })
      .catch(() => {
        /* non-critical: leave the badge hidden if settings can't load */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await servingApi.getHitlQueue(chapterId ? { chapterId } : {});
        if (!cancelled) {
          setItems(res.items ?? []);
          setLoadError(false);
        }
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [chapterId, reloadKey]);

  const open = openId ? items.find((i) => i.queue_item_id === openId) ?? null : null;

  // Close on Escape; move focus into the dialog when it opens.
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);

  const refresh = () => {
    setLoading(true);
    setNotice(null);
    setReloadKey((k) => k + 1);
  };

  const draftFor = (it: HitlItem): Draft =>
    drafts[it.queue_item_id] ?? { score: aiSuggestion(it), feedback: "" };

  const setDraft = (it: HitlItem, patch: Partial<Draft>) =>
    setDrafts((prev) => ({ ...prev, [it.queue_item_id]: { ...draftFor(it), ...patch } }));

  const openDialog = (it: HitlItem) => {
    setDialogError(null);
    setOpenId(it.queue_item_id);
  };

  const handleRelease = async (it: HitlItem) => {
    const d = draftFor(it);
    if (d.score == null) {
      setDialogError(t.pickScore);
      return;
    }
    if (d.score < 0 || d.score > maxFor(it)) {
      setDialogError(t.outOfRange(maxFor(it)));
      return;
    }
    setDialogError(null);
    setBusy(true);
    try {
      const res = await servingApi.releaseScore({
        queue_item_id: it.queue_item_id,
        score: d.score,
        max_score: maxFor(it),
        feedback: d.feedback.trim() || undefined,
        ltik,
      });
      const score = typeof res?.released_score === "number" ? res.released_score : d.score;
      setReleased((prev) => ({ ...prev, [it.queue_item_id]: score }));
      setItems((prev) => prev.filter((q) => q.queue_item_id !== it.queue_item_id));
      setNotice(t.released(it.student_name, score, maxFor(it)));
      setOpenId(null);
    } catch {
      setDialogError(t.releaseFailed);
    } finally {
      setBusy(false);
    }
  };

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(lang === "es" ? "es-US" : "en-US", { month: "short", day: "numeric" });
  };

  const tierNum = (tier: string) => String(tier).replace(/^T/i, "");
  const nSpoken = items.filter(isSpoken).length;
  const nWritten = items.length - nSpoken;
  const nReleased = Object.keys(released).length;

  const outer = embedded ? "" : "min-h-screen";
  const outerStyle = embedded ? undefined : { background: chassis.background };
  const inner = embedded ? "" : "max-w-3xl mx-auto px-4 py-8";

  const renderAnswer = (it: HitlItem) =>
    isSpoken(it) ? (
      it.audio_url ? (
        <div className="mb-3">
          <audio controls preload="none" src={it.audio_url} className="w-full" aria-label={t.spoken}>
            <track kind="captions" />
          </audio>
          <p className="text-xs mt-1" style={{ color: chassis.textSecondary }}>{t.listenHelp}</p>
        </div>
      ) : (
        <p className="text-sm italic mb-3" style={{ color: chassis.textSecondary }}>{t.noAudio}</p>
      )
    ) : (
      <div className="text-sm mb-3 px-3 py-2 rounded bg-gray-50" style={{ borderLeft: `3px solid ${chassis.accent}` }}>
        {it.response_text ? (
          <p className="whitespace-pre-wrap break-words" style={{ color: chassis.textPrimary }}>{it.response_text}</p>
        ) : (
          <p className="italic" style={{ color: chassis.textSecondary }}>{t.noText}</p>
        )}
      </div>
    );

  return (
    <div className={outer} style={outerStyle} data-testid="review-answers">
      <div className={inner}>
        <div className="bg-white rounded-lg border p-4 sm:p-6 mb-4" style={{ borderColor: "rgb(229 231 235)" }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>
                {chapterId ? t.eyebrowChapter(chapterId) : t.eyebrowAll}
              </p>
              <h1 className="text-2xl font-bold" style={{ color: chassis.textPrimary }}>
                {t.title}
              </h1>
              {!loading && !loadError && (
                <p className="text-sm mt-1" style={{ color: chassis.textSecondary }} data-testid="review-count">
                  {t.waiting(items.length)}
                  {items.length > 0 && ` · ${t.mix(nSpoken, nWritten)}`}
                  {nReleased > 0 && ` · ${t.releasedThisSession(nReleased)}`}
                </p>
              )}
              {aiAutoRelease !== null && (
                <span
                  className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: "rgb(243 244 246)", color: chassis.textSecondary }}
                >
                  {t.aiAutoRelease(aiAutoRelease)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={refresh}
              className="shrink-0 text-sm font-medium px-3 py-1.5 rounded-md border"
              style={{ borderColor: chassis.accent, color: chassis.accent }}
            >
              {t.refresh}
            </button>
          </div>
        </div>

        {notice && (
          <p role="status" className="text-sm mb-4 px-3 py-2 rounded-md bg-green-50 text-green-800 border border-green-200">
            {notice}
          </p>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-3 py-12" role="status">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: chassis.accent }} />
            <span className="text-sm" style={{ color: chassis.textSecondary }}>{t.loading}</span>
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-600 mb-4">{t.loadFailed}</p>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center" style={{ borderColor: "rgb(229 231 235)" }} data-testid="review-empty">
            <p className="font-semibold mb-1" style={{ color: chassis.textPrimary }}>{t.emptyTitle}</p>
            <p className="text-sm" style={{ color: chassis.textSecondary }}>{t.emptyBody}</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {items.map((it) => (
              <li
                key={it.queue_item_id}
                className="bg-white rounded-lg border p-4 sm:p-5"
                style={{ borderColor: "rgb(229 231 235)" }}
                data-testid="review-item"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="font-semibold" style={{ color: chassis.textPrimary }}>
                    {it.student_name}
                  </span>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: chassis.accent, color: chassis.accentText }}>
                      {isSpoken(it) ? t.spoken : t.written}
                    </span>
                    <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgb(243 244 246)", color: chassis.textSecondary }}>
                      {t.tier(tierNum(it.tier))}
                    </span>
                    <span className="px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgb(243 244 246)", color: chassis.textSecondary }}>
                      {LANG_LABELS[it.language] ?? it.language}
                    </span>
                  </div>
                </div>

                <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>{t.question}</p>
                <p className="text-sm mb-3 break-words" style={{ color: chassis.textSecondary }}>{it.prompt}</p>

                <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>{t.answer}</p>
                {renderAnswer(it)}

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-xs" style={{ color: chassis.textSecondary }}>
                    {[fmtDate(it.submitted_at) && t.submittedOn(fmtDate(it.submitted_at)), t.maxPoints(maxFor(it))]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <button
                    type="button"
                    onClick={() => openDialog(it)}
                    className="px-5 py-2 rounded-md font-medium"
                    style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
                  >
                    {t.scoreButton}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
          onClick={() => !busy && setOpenId(null)}
        >
          <div
            ref={dialogRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="review-dialog-title"
            className="bg-white w-full sm:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-xl sm:rounded-xl p-4 sm:p-6 outline-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="review-dialog"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <h2 id="review-dialog-title" className="text-lg font-bold" style={{ color: chassis.textPrimary }}>
                  {t.dialogTitle}
                </h2>
                <p className="text-sm" style={{ color: chassis.textSecondary }}>
                  {open.student_name} · {isSpoken(open) ? t.spoken : t.written} · {t.tier(tierNum(open.tier))}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                disabled={busy}
                className="shrink-0 text-sm px-2 py-1 rounded-md border"
                style={{ borderColor: "rgb(209 213 219)", color: chassis.textSecondary }}
              >
                {t.close}
              </button>
            </div>

            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>{t.question}</p>
            <p className="text-sm mb-3 break-words" style={{ color: chassis.textSecondary }}>{open.prompt}</p>
            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: chassis.icon }}>{t.answer}</p>
            {renderAnswer(open)}

            {open.ai_preliminary_feedback && (
              <p className="text-xs mb-2 italic break-words" style={{ color: chassis.textSecondary }}>
                {t.aiNote}: {open.ai_preliminary_feedback}
              </p>
            )}
            {aiSuggestion(open) != null && (
              <p className="text-xs mb-2" style={{ color: "rgb(180 83 9)" }}>
                {t.aiSuggestion(aiSuggestion(open)!, maxFor(open))}
              </p>
            )}

            <fieldset className="mb-4">
              <legend className="text-sm font-semibold mb-1" style={{ color: chassis.textPrimary }}>
                {t.rubric}
              </legend>
              <p className="text-xs mb-2" style={{ color: chassis.textSecondary }}>
                {open.rubric?.criteria?.length ? t.rubricHelp(maxFor(open)) : t.noRubric}
              </p>
              <div className="space-y-2">
                {levelsFor(open).map((lv) => {
                  const selected = draftFor(open).score === lv.score;
                  return (
                    <label
                      key={lv.score}
                      className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer"
                      style={{
                        borderColor: selected ? chassis.accent : "rgb(229 231 235)",
                        backgroundColor: selected ? "rgb(238 242 255)" : "white",
                      }}
                    >
                      <input
                        type="radio"
                        name={`score-${open.queue_item_id}`}
                        value={lv.score}
                        checked={selected}
                        onChange={() => setDraft(open, { score: lv.score })}
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold" style={{ color: chassis.textPrimary }}>
                          {t.points(lv.score)}
                        </span>
                        {lv.descriptor && (
                          <span className="block text-sm break-words" style={{ color: chassis.textSecondary }}>
                            {lv.descriptor}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <label className="block text-sm mb-4" style={{ color: chassis.textPrimary }}>
              <span className="block mb-1">{t.feedback}</span>
              <textarea
                rows={2}
                value={draftFor(open).feedback}
                onChange={(e) => setDraft(open, { feedback: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
                style={{ borderColor: "rgb(209 213 219)" }}
              />
            </label>

            {dialogError && <p className="text-sm text-red-600 mb-3" role="alert">{dialogError}</p>}

            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpenId(null)}
                disabled={busy}
                className="px-4 py-2 rounded-md border text-sm font-medium"
                style={{ borderColor: "rgb(209 213 219)", color: chassis.textPrimary }}
              >
                {t.cancel}
              </button>
              <button
                type="button"
                onClick={() => handleRelease(open)}
                disabled={busy || draftFor(open).score == null}
                className="px-5 py-2 rounded-md font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: chassis.accent, color: chassis.accentText }}
              >
                {busy ? t.releasing : t.release}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HitlQueue;
